import { noop } from "lodash";
import { useMutation, useQuery, useQueryClient } from "react-query";
import type { BaseBlock } from "@/react/components/shared/BuilderUI/DataModel.types";
import {
  reactQueryDelete,
  reactQueryGet,
  reactQueryPost,
} from "@/react/helpers/backendRequestHelpers";
import { internalApi } from "@/react/helpers/urlHelpers";

interface CreateReusableBlockData {
  block_data: BaseBlock;
  name: string;
}

export interface ReusableBlock {
  block_data: BaseBlock;
  created_at: string;
  created_by: {
    id: number;
    name: string;
  };
  description?: string;
  id: number;
  name: string;
  updated_at: string;
}

export const REUSABLE_BLOCKS_QUERY_KEY = ["reusableBlocks"];

export const useCreateReusableBlock = ({
  onSuccess = noop,
  onError = noop,
}: {
  onSuccess?: () => void;
  onError?: () => void;
}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateReusableBlockData) =>
      reactQueryPost(internalApi.settings.sites.reusableBlocks.create(), data),
    onSuccess: () => {
      void queryClient.invalidateQueries(REUSABLE_BLOCKS_QUERY_KEY);
      onSuccess();
    },
    onError,
  });
};

export const useGetReusableBlocks = () =>
  useQuery({
    queryKey: REUSABLE_BLOCKS_QUERY_KEY,
    queryFn: () =>
      reactQueryGet<ReusableBlock[]>(
        internalApi.settings.sites.reusableBlocks.index(),
      ),
    staleTime: 1000 * 60 * 5,
  });

export const useDeleteReusableBlock = ({
  onSuccess = noop,
  onError = noop,
}: {
  onSuccess?: () => void;
  onError?: () => void;
} = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (blockId: number) =>
      reactQueryDelete(
        internalApi.settings.sites.reusableBlocks.destroy({ id: blockId }),
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries(REUSABLE_BLOCKS_QUERY_KEY);
      onSuccess();
    },
    onError,
  });
};
